import axios, { AxiosError } from 'axios';

// Which backend this build talks to. CRA bakes REACT_APP_* into the bundle at build
// time, so each environment gets its own build (see .gitlab-ci.yml) — the fallback is
// only the local-dev default.
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://ai.staging.nuway.co';

// The session lives in an HttpOnly cookie set by the backend, so JS never sees the
// token: every client sends credentials and there is no Authorization header.
//
// Exception — local development: a page on http://localhost calling the staging API is
// cross-site, so the browser drops the cookie. Only there we fall back to a Bearer token
// kept in localStorage (the login response still returns it). Never used on real hosts.
export const IS_LOCAL_DEV = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

const DEV_TOKEN_KEY = 'adminToken';

export const getDevToken = (): string | null => {
  if (!IS_LOCAL_DEV) return null;
  try {
    return localStorage.getItem(DEV_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setDevToken = (token?: string | null) => {
  if (!IS_LOCAL_DEV) return;
  try {
    if (token) localStorage.setItem(DEV_TOKEN_KEY, token);
    else localStorage.removeItem(DEV_TOKEN_KEY);
  } catch {
    // storage unavailable — nothing to do
  }
};

// A 401 means the session expired: tell the app so it can drop back to /login. A 403
// means "logged in, but not a platform admin" — that must NOT log the user out, the
// calling screen shows it inline. /auth/* calls are exempt: a 401 from /auth/me is the
// normal "signed out" answer and a 401 from login is just a wrong password.
export const SESSION_EXPIRED_EVENT = 'admin:session-expired';

const errorHandler = (error: AxiosError<any>) => {
  const url = error?.config?.url || '';
  if (error?.response?.status === 401 && !url.includes('/auth/')) {
    setDevToken(null);
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
  return Promise.reject((error?.response && error.response.data) || 'Wrong Services');
};

const createClient = () => {
  const client = axios.create({ baseURL: API_BASE_URL, withCredentials: !IS_LOCAL_DEV });
  client.interceptors.request.use((config) => {
    const token = getDevToken();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    return config;
  });
  client.interceptors.response.use((r) => r, errorHandler);
  return client;
};

// The pages carried over from front-end import both names; in this app they are the
// same kind of client, since every call here is an admin call.
export const axiosAdmin = createClient();
const axiosServices = createClient();

export default axiosServices;
